import React from "react";
import { useDispatch } from "react-redux";
import { getLocalizedString } from "@icarius-localization/strings";
import paths from "@icarius-router/paths";
import CommonPage from "@icarius-common/commonPage";
import { Typography } from "@material-ui/core";
import ProgressRenderer from "@icarius-table/renderersAndFilters/progressRenderer";
import ProgressFilter from "@icarius-table/renderersAndFilters/progressFilter";
import IndicatorRenderer from "@icarius-table/renderersAndFilters/indicatorRenderer";
import CustomIconButton from "@icarius-common/abmComponents/customIconButton";
import useTemplates from "@icarius-common/templates/components/useTemplates";
import CreateTemplateDialog from "@icarius-common/templates/components/createTemplateDialog";
import TemplatesHeader from "@icarius-common/templates/components/templatesHeader";
import { openDialogAction } from "@icarius-common/dialog/actions";
import useCollaboratorSkillsRanking from "./useCollaboratorSkillsRanking";
import SectionSelector from "./sectionSelector";
import ResultRenderer from "./gridRenderers/resultRenderer";
import GapRenderer from "./gridRenderers/gapRenderer";
import PieRenderer from "./gridRenderers/pieRenderer";

const CollaboratorSkillsRanking = ({ history }) => {

  const dispatch = useDispatch();

  const {
    isLoading,
    summary,
    skillMatrixColumnDef,
    skillMatrix,
    suggestions,
  } = useCollaboratorSkillsRanking();

  const {
    user,
    templates,
    selectedTemplate,
    isLoadingTemplates,
    createTemplateDialogIsOpen,
    getRole,
    handleCreateTemplate,
    handleImportTemplates,
    handleExportTemplates,
    handleSelectTemplateAndUpdateGrid,
    handleOpenCreateTemplateDialog,
    handleOpenUpdateTemplateDialog,
    handleOpenDeleteTemplateDialog,
    handleCloseCreateTemplateDialog,
  } = useTemplates("COLLABORATOR_SKILLS_RANKING");

  const goBackButton = () => (
    <CustomIconButton
      title={getLocalizedString("goBack")}
      type={"goBack"}
      onClick={() => history.push(paths.trainingAndDevelopment)}
    />
  )

  const matrixExplanationButton = () => skillMatrix?.levelNames && (
    <CustomIconButton
      title={getLocalizedString("masteryLevel")}
      onClick={() => {
        dispatch(openDialogAction({
          title: getLocalizedString("masteryLevel"),
          msg: (
            <div style={{ display: "flex", flexDirection: "column", gap: 15, minWidth: 250 }}>
              {
                skillMatrix.levelNames.map((levelName, levelIndex) => {
                  return (
                    <div
                      key={levelIndex}
                      style={{ display: "flex", gap: 10, alignItems: "center" }}
                    >
                      <PieRenderer value={levelIndex} size={28} />
                      <Typography className="whiteText">
                        {levelName} ({levelIndex + 1})
                      </Typography>
                    </div>
                  )
                })
              }
            </div>
          ),
          acceptOnly: true,
        }))
      }}
    >
      <PieRenderer value={3} size={24} />
    </CustomIconButton>
  )

  const templatesHeader = (gridRef) => (
    <>
      <div style={{ paddingLeft: 10, display: "flex" }}>
        <TemplatesHeader
          gridRef={gridRef}
          user={user}
          templates={templates}
          selectedTemplate={selectedTemplate}
          getRole={getRole}
          handleImportTemplates={handleImportTemplates}
          handleExportTemplates={handleExportTemplates}
          handleOpenCreateTemplateDialog={handleOpenCreateTemplateDialog}
          handleOpenDeleteTemplateDialog={handleOpenDeleteTemplateDialog}
          handleOpenUpdateTemplateDialog={handleOpenUpdateTemplateDialog}
          handleSelectTemplateAndUpdateGrid={handleSelectTemplateAndUpdateGrid}
        />
      </div>
    </>
  )

  return (
    <SectionSelector
      sections={[
        {
          key: 0,
          title: getLocalizedString("collaboratorRanking"),
          subtitle: getLocalizedString("byPositionSkills"),
          content: (
            <CommonPage
              key={0}
              isLoading={isLoading || isLoadingTemplates}
              gridTitle={getLocalizedString("skills")}
              columnDefPage={paths.collaboratorSkillsRanking}
              customHeader={templatesHeader}
              menuItems={[goBackButton]}
              rowData={summary}
              frameworkComponents={{ GapRenderer, IndicatorRenderer, ProgressRenderer, ProgressFilter, ResultRenderer }}
              hasHelp
              hasExpand
              hasSelectAll
            >
              {
                (gridRef) => {
                  return (
                    <>
                      {
                        createTemplateDialogIsOpen &&
                        <CreateTemplateDialog
                          open={createTemplateDialogIsOpen}
                          level={user.level}
                          handleCreateTemplate={(role, name) => handleCreateTemplate(gridRef, role, name)}
                          handleCloseDialog={handleCloseCreateTemplateDialog}
                        />
                      }
                    </>
                  )
                }
              }
            </CommonPage>
          ),
        },
        {
          key: 1,
          title: getLocalizedString("skillMatrix"),
          subtitle: getLocalizedString("currentDevelopmentLevel"),
          content: (
            <CommonPage
              key={1}
              isLoading={isLoading}
              gridTitle={getLocalizedString("skillMatrix")}
              ownColumnDef={skillMatrixColumnDef}
              menuItems={[goBackButton, matrixExplanationButton]}
              rowData={skillMatrix?.members}
              frameworkComponents={{ PieRenderer }}
              ignoreProcessesbyPath
              hasHelp
              hasExpand
              hasSelectAll
            />
          ),
        },
        {
          key: 2,
          title: getLocalizedString("suggestedCourses"),
          subtitle: getLocalizedString("toImproveDevelopment"),
          content: (
            <CommonPage
              key={2}
              isLoading={isLoading}
              gridTitle={getLocalizedString("suggestedCourses")}
              columnDefPage={paths.collaboratorSkillsRankingSuggestedSkills}
              menuItems={[goBackButton]}
              rowData={suggestions}
              ignoreProcessesbyPath
              hasHelp
              hasExpand
              hasSelectAll
            />
          ),
        },
      ]}
    />
  );
}

export default CollaboratorSkillsRanking;